import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { SocialOpportunityItem } from '@inference.engine/widgets/web';

import { UNCATEGORIZED_TARGET_AUDIENCE } from '../../social-media-opportunities-widget.constants';

import { DEFAULT_FILTERS } from './useOpportunityFilters.constants';
import type {
  OpportunityFilters,
  UseOpportunityFiltersReturn,
} from './useOpportunityFilters.types';
import { OpportunityItemWithFavorite } from '../../../ui/components/OpportunitiesList/OpportunitiesList.types';

export const useOpportunityFilters = (
  opportunities: OpportunityItemWithFavorite[] = [],
): UseOpportunityFiltersReturn => {
  const { t } = useTranslation('analysis');
  const [filters, setFiltersState] = useState<OpportunityFilters>(DEFAULT_FILTERS);

  const audienceOptions = useMemo(() => {
    const audiencesSet = new Set<string>();
    opportunities.forEach((opp) => {
      // Backward compatibility: handle both string (old) and array (new) formats
      const rawValue = opp.targetAudience as string | string[] | undefined;
      const targetAudiences = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];

      if (targetAudiences.length > 0) {
        targetAudiences.forEach((audience) => {
          if (audience.trim()) {
            audiencesSet.add(audience.trim());
          }
        });
      } else {
        audiencesSet.add(UNCATEGORIZED_TARGET_AUDIENCE);
      }
    });
    // The value stays the raw bucket key so `byTargetAudience` can match it; only
    // the label the user reads is translated.
    return Array.from(audiencesSet)
      .sort()
      .map((audience) => ({
        value: audience,
        label:
          audience === UNCATEGORIZED_TARGET_AUDIENCE
            ? t('socialMediaOpportunitiesWidget.useOpportunityFilters.uncategorized')
            : audience,
      }));
  }, [opportunities, t]);

  const byTargetAudience = useCallback(
    (item: SocialOpportunityItem): boolean => {
      if (filters.targetAudiences.length === 0) return true;

      // Backward compatibility: handle both string (old) and array (new) formats
      const rawValue = item.targetAudience as string | string[] | undefined;
      const targetAudiences = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];

      const normalizedAudiences =
        targetAudiences.length > 0 ? targetAudiences : [UNCATEGORIZED_TARGET_AUDIENCE];

      return normalizedAudiences.some((audience) =>
        filters.targetAudiences.includes(audience.trim()),
      );
    },
    [filters.targetAudiences],
  );

  // Main filtering logic
  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(byTargetAudience);
  }, [opportunities, byTargetAudience]);

  // Split into top and recent opportunities
  const topOpportunities = useMemo(() => {
    return filteredOpportunities.filter((opp) => opp.topRanked);
  }, [filteredOpportunities]);

  const recentOpportunities = useMemo(() => {
    return filteredOpportunities.filter((opp) => !opp.topRanked);
  }, [filteredOpportunities]);

  // Computed values
  const hasActiveFilters = useMemo(() => filters.targetAudiences.length > 0, [filters]);

  // Action handlers
  const updateFilter = useCallback(
    <K extends keyof OpportunityFilters>(key: K, value: OpportunityFilters[K]) => {
      setFiltersState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  return {
    // Data
    filteredOpportunities,
    topOpportunities,
    recentOpportunities,
    filters,

    // Actions
    updateFilter,
    resetFilters,

    // Computed
    audienceOptions,
    hasActiveFilters,
  };
};
